import { Trans } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import { Alert, Select, Checkbox, MenuItem, TextField, InputLabel, Typography, FormControlLabel } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { RejectionReason } from 'src/services/applications/applications.types';
import { UserManagedRejectionReasons } from 'src/services/applications/applications.utils';
import { useLazyGetApplicationByIdQuery } from 'src/services/applications/applications.service';
import { IRejectionTemplate, OrganizationSetting } from 'src/services/organisation/organisation.types';
import { useGetOrganizationSettingsQuery, useGetRejectionTemplateSettingsQuery } from 'src/services/organisation/organisation.service';

import { ConfirmDialog } from 'src/components/custom-dialog';

type Props = {
  open: boolean;
  applications: string[],
  onConfirm: (rejectionReason: RejectionReason, rejectionMessage: string, shouldNotify: boolean) => void;
  onClose: VoidFunction;
};

export default function ApplicationRejectionConfirm({ open, onConfirm, onClose, applications = [] }: Props) {

  const { organization } = useAuthContext();

  const { t } = useTranslate();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<RejectionReason>();
  const [rejectionFeedback, setRejectionFeedback] = useState<string>('');
  const [shouldNotify, setShouldNotify] = useState<boolean>(true);
  const [showSilentRejectionSelector, setShowSilentRejectionSelector] = useState<boolean>(false);

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const { currentData: rejectionTemplates } = useGetRejectionTemplateSettingsQuery(
    organization.id
  );

  const [getApplicationById] = useLazyGetApplicationByIdQuery();

  const rejectionReasonsEditableSetting = useMemo(() => {

    if (settingsData) {
      const rejectionTemplateSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.REJECTION_TEMPLATE_MESSAGES_EDITABLE
      );

      if (rejectionTemplateSetting) {
        return rejectionTemplateSetting.setting_value as boolean;
      }
    }

    return false;
  }, [settingsData]);

  const silentRejectionAllowedSetting = useMemo(() => {

    if (settingsData) {
      const silentRejectionSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.SILENT_REJECTION_ENABLED
      );

      if (silentRejectionSetting) {
        return silentRejectionSetting.setting_value as boolean;
      }
    }

    return false;

  }, [settingsData]);

  useEffect(() => {
    const checkVisibility = async () => {
      // to show the silent rejection selector, the following conditions must be met:
      // 1. silent rejection must be allowed in organization settings
      // 2. only one application is being rejected in this action
      // 3. the current user is the manager of the application (not a client in tri party application)
      if (applications.length === 1 && silentRejectionAllowedSetting) {

        const applicationId = applications[0];
        const application = await getApplicationById(applicationId).unwrap();

        if (application.manager.id === organization.id) {
          setShowSilentRejectionSelector(true);
          return;
        }
      }

      // in all other cases, hide the selector 
      setShowSilentRejectionSelector(false);
    }

    if (open) {
      checkVisibility();
    }

  }, [open, applications, silentRejectionAllowedSetting, getApplicationById, organization]);

  useEffect(() => {
    if (rejectionTemplates && rejectionTemplates.length > 0) {
      const selectedTemplate: IRejectionTemplate = rejectionTemplates.find((template) => template.reason === rejectionReason) as IRejectionTemplate;

      if (selectedTemplate) {
        setRejectionFeedback(selectedTemplate.message);
      }
    }
  }, [rejectionReason, rejectionTemplates]);

  const handleReject = () => {
    setErrorMessage('');
    if (!rejectionReason) {
      setErrorMessage(t('applications.rejection.reason_required'));
      return;
    }

    onConfirm(rejectionReason as RejectionReason, rejectionFeedback, shouldNotify);
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={t('common.confirm')}
      content={
        <Stack spacing={2} direction="column">
          <Box>
            <Trans>
              {t('applications.selected', { count: applications.length })}
            </Trans>
          </Box>
          <Box>
            {t('applications.stage_change.reject_applications')}
          </Box>

          {
            errorMessage && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errorMessage}
              </Typography>
            )
          }

          <Stack>
            <InputLabel required>{t('applications.rejection.reason_label')}</InputLabel>
            <Select
              required
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value as RejectionReason)}
            >
              {UserManagedRejectionReasons.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack>
            <InputLabel>{t('applications.rejection.feedback_label')}</InputLabel>
            <TextField
              value={rejectionFeedback}
              disabled={!rejectionReasonsEditableSetting}
              onChange={(e) => setRejectionFeedback(e.target.value)}
              multiline
              rows={6}
            />
            {
              !rejectionReasonsEditableSetting && (
                <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                  {t('applications.rejection.editable_info')}
                </Alert>
              )
            }
          </Stack>

          {
            showSilentRejectionSelector && (
              <Stack>
                <FormControlLabel
                  label={
                    <Stack gap={0} sx={{ ml: 1 }}>
                      <Typography variant="body2">
                        {t('applications.rejection.should_notify')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('applications.rejection.should_notify_helper')}
                      </Typography>
                    </Stack>
                  }
                  control={<Checkbox checked={shouldNotify} onChange={(e) => setShouldNotify(e.target.checked)} />}
                />
              </Stack>
            )
          }
        </Stack>
      }
      action={
        <LoadingButton variant="contained" color="error" onClick={handleReject}>
          {t('common.confirm')}
        </LoadingButton>
      }
    />
  );
}
